window.FoxTheme = window.FoxTheme || {};

/**
 * Shared native-scroll controller: custom scrollbar thumb, drag-to-scroll,
 * tap-vs-drag disambiguation and arrow-button paging for any overflow-x/y
 * auto container. Used by the product media thumbnails and the collection
 * sub-category slider so both share one implementation.
 *
 * @param {Object} options
 * @param {HTMLElement} options.scroller - the overflow auto element
 * @param {HTMLElement} options.list - the flex row inside the scroller
 * @param {{el: HTMLElement, track: HTMLElement, thumb: HTMLElement}} [options.scrollbar]
 * @param {HTMLElement} [options.prevBtn]
 * @param {HTMLElement} [options.nextBtn]
 * @param {(event: Event) => void} [options.onActivate] - called on tap/click that wasn't a drag
 * @returns {{ updateScrollbar: () => void } | null}
 */
window.FoxTheme.initDragScroller = function initDragScroller(options) {
  const { scroller, list, scrollbar, prevBtn, nextBtn, onActivate } = options || {};
  if (!scroller || !list) return null;

  const isVertical = () => getComputedStyle(list).flexDirection === 'column';

  const sb = scrollbar && scrollbar.el;
  const track = scrollbar && scrollbar.track;
  const thumb = scrollbar && scrollbar.thumb;

  const updateScrollbar = () => {
    if (!sb || !track || !thumb) return;
    const vertical = isVertical();
    const scrollSize = vertical ? scroller.scrollHeight : scroller.scrollWidth;
    const clientSize = vertical ? scroller.clientHeight : scroller.clientWidth;
    const maxScroll = scrollSize - clientSize;

    if (maxScroll <= 1) {
      sb.classList.remove('is-overflow');
      return;
    }

    sb.classList.add('is-overflow');

    const trackSize = vertical ? track.offsetHeight : track.offsetWidth;
    const thumbSize = Math.max(40, Math.round((clientSize / scrollSize) * trackSize));
    const maxPos = trackSize - thumbSize;
    const scrollPos = vertical ? scroller.scrollTop : scroller.scrollLeft;
    const pos = maxScroll > 0 ? Math.round((scrollPos / maxScroll) * maxPos) : 0;

    if (vertical) {
      thumb.style.setProperty('--sb-height', thumbSize + 'px');
      thumb.style.setProperty('--sb-top', pos + 'px');
    } else {
      thumb.style.setProperty('--sb-width', thumbSize + 'px');
      thumb.style.setProperty('--sb-left', pos + 'px');
    }
  };

  scroller.addEventListener('scroll', updateScrollbar, { passive: true });
  window.addEventListener('resize', updateScrollbar);
  updateScrollbar();
  setTimeout(updateScrollbar, 100);
  setTimeout(updateScrollbar, 500);

  if (sb && track && thumb) {
    thumb.addEventListener('mousedown', (e) => {
      const vertical = isVertical();
      const start = vertical ? e.clientY : e.clientX;
      const startScroll = vertical ? scroller.scrollTop : scroller.scrollLeft;
      const scrollSize = vertical ? scroller.scrollHeight : scroller.scrollWidth;
      const clientSize = vertical ? scroller.clientHeight : scroller.clientWidth;
      const maxScroll = scrollSize - clientSize;
      const trackSize = vertical ? track.offsetHeight : track.offsetWidth;
      const thumbSize = vertical ? thumb.offsetHeight : thumb.offsetWidth;
      const maxPos = trackSize - thumbSize;

      thumb.classList.add('is-dragging');
      e.preventDefault();
      e.stopPropagation();

      const onMove = (ev) => {
        if (maxPos <= 0 || maxScroll <= 0) return;
        const current = vertical ? ev.clientY : ev.clientX;
        const delta = current - start;
        const next = Math.max(0, Math.min(startScroll + (delta / maxPos) * maxScroll, maxScroll));
        if (vertical) {
          scroller.scrollTop = next;
        } else {
          scroller.scrollLeft = next;
        }
      };

      const onUp = () => {
        thumb.classList.remove('is-dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    track.addEventListener('click', (e) => {
      if (e.target === thumb) return;
      const vertical = isVertical();
      const rect = track.getBoundingClientRect();
      const scrollSize = vertical ? scroller.scrollHeight : scroller.scrollWidth;
      const clientSize = vertical ? scroller.clientHeight : scroller.clientWidth;
      const maxScroll = scrollSize - clientSize;
      const trackSize = vertical ? track.offsetHeight : track.offsetWidth;
      const thumbSize = vertical ? thumb.offsetHeight : thumb.offsetWidth;
      const maxPos = trackSize - thumbSize;
      const clickPos = (vertical ? e.clientY - rect.top : e.clientX - rect.left) - thumbSize / 2;
      const next = maxPos > 0 ? (Math.max(0, Math.min(clickPos, maxPos)) / maxPos) * maxScroll : 0;
      if (vertical) {
        scroller.scrollTop = next;
      } else {
        scroller.scrollLeft = next;
      }
    });
  }

  let dragging = false;
  let dragVertical = false;
  let moved = false;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let startScrollTop = 0;

  scroller.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    dragging = true;
    moved = false;
    dragVertical = isVertical();
    startX = e.clientX;
    startY = e.clientY;
    startScrollLeft = scroller.scrollLeft;
    startScrollTop = scroller.scrollTop;
  });

  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (!moved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      moved = true;
      scroller.classList.add('is-drag-scrolling');
    }

    if (moved) {
      if (dragVertical) {
        scroller.scrollTop = startScrollTop - dy;
      } else {
        scroller.scrollLeft = startScrollLeft - dx;
      }
    }
  });

  const endDrag = () => {
    if (dragging) {
      dragging = false;
      scroller.classList.remove('is-drag-scrolling');
    }
  };
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('mouseleave', endDrag);

  if (typeof onActivate === 'function') {
    list.addEventListener('click', (e) => {
      if (moved) return;
      onActivate(e);
    });

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartScroll = 0;

    scroller.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartScroll = isVertical() ? scroller.scrollTop : scroller.scrollLeft;
      },
      { passive: true }
    );

    scroller.addEventListener(
      'touchend',
      (e) => {
        const t = e.changedTouches[0];
        const vertical = isVertical();
        const scrollNow = vertical ? scroller.scrollTop : scroller.scrollLeft;
        const tapMoved =
          Math.abs(t.clientX - touchStartX) > 8 ||
          Math.abs(t.clientY - touchStartY) > 8 ||
          Math.abs(scrollNow - touchStartScroll) > 5;
        if (tapMoved) return;
        onActivate(e);
      },
      { passive: false }
    );
  }

  const step = () => {
    const item = list.firstElementChild;
    if (!item) return 100;
    const vertical = isVertical();
    const gap = parseFloat(getComputedStyle(list)[vertical ? 'rowGap' : 'columnGap']) || 0;
    return (vertical ? item.offsetHeight : item.offsetWidth) + gap;
  };

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      scroller.scrollBy(isVertical() ? { top: -step() } : { left: -step() });
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      scroller.scrollBy(isVertical() ? { top: step() } : { left: step() });
    });
  }

  return { updateScrollbar };
};
